/**
 * Descoberta OIDC — os endpoints do realm, lidos do próprio provedor.
 *
 * ── Por que não deixar as URLs em configuração ────────────────────────────
 *
 * Seria mais rápido escrever `${base}/realms/${realm}/protocol/openid-connect/auth`
 * e acabar. Só que aí a aplicação passa a afirmar a topologia do Keycloak, e
 * qualquer mudança dele — versão nova, caminho diferente, provedor trocado —
 * quebra a tela sem quebrar nenhum teste.
 *
 * O documento de descoberta é o contrato que o provedor publica sobre si mesmo.
 * Ler dele é a diferença entre depender do que ele PROMETE e depender do que a
 * gente supôs que ele faz.
 *
 * ── O cache é por realm, não global ───────────────────────────────────────
 *
 * Um realm por tenant (ADR-0006). Cache global devolveria o `token_endpoint` da
 * revenda errada para quem trocasse de conta na mesma aba — falha silenciosa e
 * dificílima de ver, porque o pedido chega a um provedor que existe.
 */

const CAMPOS_OBRIGATORIOS = [
  'issuer',
  'authorization_endpoint',
  'token_endpoint',
  'end_session_endpoint',
] as const;

export interface Descoberta {
  readonly issuer: string;
  readonly authorization_endpoint: string;
  readonly token_endpoint: string;
  readonly end_session_endpoint: string;
  readonly revocation_endpoint?: string;
}

export class ProvedorIndisponivelError extends Error {
  constructor(
    readonly realm: string,
    motivo: string,
  ) {
    super(`Não foi possível falar com o provedor de identidade da organização: ${motivo}`);
    this.name = 'ProvedorIndisponivelError';
  }
}

const cache = new Map<string, Promise<Descoberta>>();

/** Só para teste: devolve o cache ao estado inicial. */
export function esquecerDescoberta(): void {
  cache.clear();
}

/**
 * Valida a resposta antes de confiar nela.
 *
 * Sem isto, um `undefined` num campo viraria a string "undefined" no meio de
 * uma URL e o erro apareceria como "página não encontrada" no provedor — três
 * camadas longe da causa.
 */
export function interpretarDescoberta(bruto: unknown, realm: string): Descoberta {
  if (typeof bruto !== 'object' || bruto === null) {
    throw new ProvedorIndisponivelError(realm, 'a descoberta não veio como objeto');
  }

  const registro = bruto as Record<string, unknown>;
  for (const campo of CAMPOS_OBRIGATORIOS) {
    if (typeof registro[campo] !== 'string' || registro[campo] === '') {
      throw new ProvedorIndisponivelError(realm, `a descoberta não trouxe \`${campo}\``);
    }
  }

  return registro as unknown as Descoberta;
}

export async function descobrir(
  baseDoProvedor: string,
  realm: string,
  buscar: typeof globalThis.fetch = globalThis.fetch,
): Promise<Descoberta> {
  const existente = cache.get(realm);
  if (existente) return existente;

  const promessa = (async () => {
    const url = `${baseDoProvedor.replace(/\/+$/, '')}/realms/${encodeURIComponent(
      realm,
    )}/.well-known/openid-configuration`;

    let resposta: Response;
    try {
      resposta = await buscar(url, { headers: { accept: 'application/json' } });
    } catch (erro: unknown) {
      throw new ProvedorIndisponivelError(
        realm,
        erro instanceof Error ? erro.message : String(erro),
      );
    }

    // 404 é o caso comum e merece nome próprio: quem digitou o slug errado na
    // tela de entrada chega exatamente aqui, e "revenda não encontrada" é
    // acionável enquanto "HTTP 404" não é.
    if (resposta.status === 404) {
      throw new ProvedorIndisponivelError(realm, 'esta organização não existe');
    }
    if (!resposta.ok) {
      throw new ProvedorIndisponivelError(realm, `respondeu HTTP ${resposta.status}`);
    }

    return interpretarDescoberta(await resposta.json(), realm);
  })();

  // Falha NÃO fica em cache: provedor fora do ar por trinta segundos deixaria a
  // aba inutilizável até recarregar.
  cache.set(
    realm,
    promessa.catch((erro: unknown) => {
      cache.delete(realm);
      throw erro;
    }),
  );

  return cache.get(realm)!;
}
